import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Chip, IconButton, LinearProgress, Tooltip } from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";

import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import DataTable from "../../components/DataTable/DataTable";
import CustomSnackbar from "../Common/snackBar";
import showConfirmation from "../../utils/Conformation";
import { getPermissionData, getUserData } from "../../Actions/common";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { formatDateTime } from "../Common/DateFormat";
import {
  getModuleArticlesAction,
  deleteTutorialAction,
  getTutorialUploadStatusAction,
} from "../../Actions/KnowledgeBaseAction";
import { TutorialPlayerModal } from "./TutorialPlayerModal";
import "./Knowledgebase.css";

const isVideoUploading = (row) =>
  row?.uploadStatus === "pending" || row?.uploadStatus === "processing";

const UploadStatusBadge = ({ status }) => {
  const cfg = {
    pending: {
      label: "Queued",
      className: "kb-chip--outline",
      icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
    },
    processing: {
      label: "Uploading",
      className: "kb-chip--navy",
      icon: <SyncIcon className="kb-spin" sx={{ fontSize: 14 }} />,
    },
    completed: {
      label: "Ready",
      className: "kb-chip--success",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
    },
    failed: {
      label: "Failed",
      className: "kb-chip--outline",
      icon: (
        <ErrorOutlineIcon sx={{ fontSize: 14, color: "#b91c1c" }} />
      ),
    },
  }[status] || { label: status || "—", className: "kb-chip--outline" };

  return (
    <Chip
      className={`kb-chip ${cfg.className}`}
      icon={cfg.icon}
      label={cfg.label}
      size="small"
    />
  );
};

const UploadProgressCell = ({ row }) => {
  const status = row.uploadStatus;
  const pct = Math.max(
    0,
    Math.min(
      100,
      Number(
        row.uploadProgress != null
          ? row.uploadProgress
          : status === "completed"
            ? 100
            : status === "failed"
              ? 0
              : status === "processing"
                ? 40
                : 5,
      ) || 0,
    ),
  );

  if (status === "completed" || (!status && pct >= 100)) {
    return (
      <div className="kb-upload-progress is-done">
        <span className="kb-upload-progress-label">100%</span>
        <LinearProgress
          className="kb-list-progress is-complete"
          variant="determinate"
          value={100}
        />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="kb-upload-progress is-failed">
        <span className="kb-upload-progress-label">Failed</span>
        <LinearProgress
          className="kb-list-progress is-failed"
          variant="determinate"
          value={pct}
        />
      </div>
    );
  }

  if (!isVideoUploading(row)) {
    return <span className="kb-cell-muted">—</span>;
  }

  return (
    <div className="kb-upload-progress">
      <span className="kb-upload-progress-label">{pct}%</span>
      <LinearProgress
        className="kb-list-progress"
        variant="determinate"
        value={pct}
      />
    </div>
  );
};

const countAttachmentsByType = (attachments) => {
  const list = Array.isArray(attachments) ? attachments : [];
  const videos = list.filter((a) =>
    (a.mediaType || "").toLowerCase().startsWith("video"),
  ).length;
  const images = list.filter((a) =>
    (a.mediaType || "").toLowerCase().startsWith("image"),
  ).length;
  const docs = list.filter((a) => {
    const t = (a.mediaType || "").toLowerCase();
    return !t.startsWith("video") && !t.startsWith("image");
  }).length;
  return { videos, images, docs };
};

const ModuleListPage = () => {
  const { moduleSlug } = useParams();
  const navigate = useNavigate();
  const isAllView = moduleSlug === "all" || moduleSlug === "ALL";

  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "knowledge-base",
  );

  const [moduleInfo, setModuleInfo] = useState(null);
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(
    ITEMS_PER_PAGE_OPTIONS[0],
  );
  const [filterValues, setFilterValues] = useState({ search: "" });
  const [activeSearch, setActiveSearch] = useState("");

  const [playerModal, setPlayerModal] = useState({
    open: false,
    tutorial: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getModuleArticlesAction(moduleSlug, {
        search: activeSearch,
        page: currentPage,
        limit: selectedRowsPerPage,
      });
      setTutorials(Array.isArray(res.articles) ? res.articles : []);
      setTotalRecords(res.total || 0);
      setModuleInfo(res.module || null);
    } catch (err) {
      showSnack("Failed to load articles", "error");
    } finally {
      setLoading(false);
    }
  }, [moduleSlug, activeSearch, currentPage, selectedRowsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setCurrentPage(1);
    setFilterValues({ search: "" });
    setActiveSearch("");
  }, [moduleSlug]);

  const uploadingIds = useMemo(
    () =>
      tutorials.filter(isVideoUploading).map((t) => t.id).join(","),
    [tutorials],
  );

  // Soft-poll upload % for rows still processing (no full-table loading flash)
  useEffect(() => {
    if (!uploadingIds) return undefined;
    const ids = uploadingIds.split(",").filter(Boolean);
    const timer = setInterval(async () => {
      try {
        const results = await Promise.all(
          ids.map(async (id) => {
            try {
              return await getTutorialUploadStatusAction(id);
            } catch {
              return null;
            }
          }),
        );
        setTutorials((prev) =>
          prev.map((row) => {
            const fresh = results.find(
              (r) => r && String(r.id) === String(row.id),
            );
            if (!fresh) return row;
            return {
              ...row,
              uploadStatus: fresh.uploadStatus ?? row.uploadStatus,
              uploadProgress:
                fresh.uploadProgress != null
                  ? fresh.uploadProgress
                  : row.uploadProgress,
              uploadError: fresh.uploadError ?? row.uploadError,
              videoUrl: fresh.videoUrl ?? row.videoUrl,
              attachments: fresh.attachments ?? row.attachments,
            };
          }),
        );
      } catch (_) {
        /* ignore poll errors */
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [uploadingIds]);

  const handleDeleteTutorial = async (id) => {
    await showConfirmation("simpleDelete", {
      entityName: "Tutorial",
      entityId: id,
      customText: "You will not be able to recover this tutorial!",
      action: async (tutorialId) => await deleteTutorialAction(tutorialId),
      onSuccess: () => {
        loadData();
        showSnack("Tutorial deleted successfully.");
      },
      onError: (error) => {
        showSnack(
          error?.response?.data?.message || "Failed to delete tutorial.",
          "error",
        );
      },
      showCancelledMsg: true,
    });
  };

  const handleFilterApply = (values) => {
    const nextSearch = (values?.search || "").trim();
    setFilterValues({ search: nextSearch });
    setActiveSearch(nextSearch);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilterValues({ search: "" });
    setActiveSearch("");
    setCurrentPage(1);
  };

  const openArticle = (row) => {
    if (isVideoUploading(row)) {
      showSnack(
        "Video is still uploading. View will unlock when it reaches 100%.",
        "warning",
      );
      return;
    }
    if (row.uploadStatus === "failed") {
      showSnack(
        "Video upload failed. Please edit and re-upload the video.",
        "error",
      );
      return;
    }
    navigate(`/knowledge-base/${row.moduleSlug || moduleSlug}/${row.id}`);
  };

  const handleTableAction = (action, row) => {
    if (action === "view") {
      openArticle(row);
    } else if (action === "edit") {
      navigate(
        `/knowledge-base/${row.moduleSlug || moduleSlug}/${row.id}/edit`,
      );
    } else if (action === "delete") {
      handleDeleteTutorial(row.id);
    }
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      minWidth: 280,
      renderCell: ({ row }) => {
        const blocked = isVideoUploading(row);
        return (
          <Tooltip
            title={
              blocked
                ? "Video still uploading — view locked"
                : row.shortDescription || ""
            }
            arrow
          >
            <div
              className={`kb-cell-title${blocked ? "" : " is-clickable"}`}
              onClick={() => openArticle(row)}
              style={blocked ? { cursor: "not-allowed", opacity: 0.7 } : undefined}
            >
              <span>{row.title || "N/A"}</span>
              {row.shortDescription ? (
                <span className="kb-cell-muted">{row.shortDescription}</span>
              ) : null}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "productType",
      headerName: "Product Type",
      minWidth: 140,
      renderCell: ({ row }) => (
        <Chip
          className="kb-chip kb-chip--navy"
          label={row.productType || "—"}
          size="small"
        />
      ),
    },
    ...(isAllView
      ? [
          {
            field: "moduleName",
            headerName: "Module",
            minWidth: 160,
            renderCell: ({ row }) => (
              <Chip
                className="kb-chip kb-chip--navy"
                label={row.moduleName || "—"}
                size="small"
              />
            ),
          },
        ]
      : []),
    {
      field: "uploadStatus",
      headerName: "Status",
      minWidth: 120,
      renderCell: ({ row }) => <UploadStatusBadge status={row.uploadStatus} />,
    },
    {
      field: "uploadProgress",
      headerName: "Upload %",
      minWidth: 160,
      renderCell: ({ row }) => <UploadProgressCell row={row} />,
    },
    {
      field: "attachments",
      headerName: "Attachments",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { videos, images, docs } = countAttachmentsByType(
          row.attachments,
        );
        const total = videos + images + docs;
        if (total === 0) return <span className="kb-cell-muted">—</span>;
        return (
          <span
            className="kb-meta-stats"
            style={{ justifyContent: "flex-start" }}
          >
            {videos > 0 && (
              <span className="kb-meta-stat">
                <VideocamOutlinedIcon />
                {videos}
              </span>
            )}
            {images > 0 && (
              <span className="kb-meta-stat">
                <ImageOutlinedIcon />
                {images}
              </span>
            )}
            {docs > 0 && (
              <span className="kb-meta-stat">
                <DescriptionOutlinedIcon />
                {docs}
              </span>
            )}
          </span>
        );
      },
    },
    {
      field: "engagement",
      headerName: "Engagement",
      minWidth: 130,
      renderCell: ({ row }) => (
        <span
          className="kb-meta-stats"
          style={{ justifyContent: "flex-start" }}
        >
          <span className="kb-meta-stat is-up">
            <ThumbUpAltOutlinedIcon />
            {row.totalHelpful || 0}
          </span>
          <span className="kb-meta-stat is-down">
            <ThumbDownAltOutlinedIcon />
            {row.totalUnhelpful || 0}
          </span>
        </span>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      minWidth: 160,
      renderCell: ({ row }) => (
        <span className="kb-cell-muted">
          {formatDateTime(row.updatedAt, true)}
        </span>
      ),
    },
  ];

  const filters = [
    {
      type: "search",
      key: "search",
      name: "search",
      placeholder: "Search module knowledge base...",
    },
  ];

  return (
    <div id="kt_content_container" className="kb-page">
      <BreadcrumbsComponent
        breadcrumbs={[
          { label: "Knowledge Base", path: "/knowledge-base" },
          { label: isAllView ? "All Articles" : moduleInfo?.name || moduleSlug },
        ]}
      />

      <div className="kb-page-head">
        <div className="kb-page-head-main">
          {!isAllView && (
            <IconButton
              className="kb-back-icon"
              onClick={() => navigate("/knowledge-base")}
              aria-label="Back to Knowledge Base"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <div>
            <h1>
              {isAllView
                ? "All Knowledge Base"
                : moduleInfo
                  ? moduleInfo.name
                  : "Module Knowledge Base"}
            </h1>
            <p>
              {isAllView
                ? `${totalRecords || tutorials.length} Knowledge Base entries across all modules`
                : `${moduleInfo?.articleCount || tutorials.length} Knowledge Base`}
            </p>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={tutorials}
        loading={loading}
        totalCount={totalRecords}
        page={currentPage}
        pageSize={selectedRowsPerPage}
        filters={filters}
        filterValues={filterValues}
        actions={{
          view: true,
          edit: Boolean(hasPermission?.canEdit),
          delete: Boolean(hasPermission?.canDelete),
        }}
        isActionDisabled={(action, row) =>
          action === "view" && isVideoUploading(row)
        }
        getActionTooltip={(action, row) => {
          if (action === "view" && isVideoUploading(row)) {
            const pct = Number(row.uploadProgress) || 0;
            return `Video uploading (${pct}%) — view unlocks at 100%`;
          }
          return null;
        }}
        getRowId={(row) => row.id}
        onAction={handleTableAction}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        onPageChange={(page) => setCurrentPage(page)}
        onPageSizeChange={(size) => {
          setSelectedRowsPerPage(size);
          setCurrentPage(1);
        }}
        hasCreatePermission={Boolean(hasPermission?.canCreate)}
        addButtonText="ADD"
        onAddClick={() =>
          navigate(
            isAllView
              ? "/knowledge-base/new"
              : `/knowledge-base/${moduleSlug}/new`,
          )
        }
      />

      <TutorialPlayerModal
        open={playerModal.open}
        onClose={() => setPlayerModal({ open: false, tutorial: null })}
        tutorial={playerModal.tutorial}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      />
    </div>
  );
};

export default ModuleListPage;
